// src/lib/relevance-scorer.ts
// Scores evidence relevance against a specific contract

import {
  EnrichedEvidenceItem,
  ScoredEvidenceItem,
  RelevanceScore,
  RelevanceBreakdown,
  ContractForMatching
} from '../types/source-grounded';

import {
  RelevanceTier,
  RelevanceWeights,
  DEFAULT_RELEVANCE_WEIGHTS,
  RELEVANCE_TIER_THRESHOLDS
} from './taxonomy/types';

import {
  calculateNaicsSimilarity,
  findAgency,
  searchAgencies
} from './taxonomy';

/**
 * Configuration for the relevance scorer
 */
export interface RelevanceScorerConfig {
  weights: RelevanceWeights;
  recencyDecayYears: number;         // Evidence older than this gets minimum recency score
  valueTolerancePercent: number;     // How much value difference is acceptable
}

const DEFAULT_CONFIG: RelevanceScorerConfig = {
  weights: DEFAULT_RELEVANCE_WEIGHTS,
  recencyDecayYears: 5,
  valueTolerancePercent: 200         // Within 200% of contract value
};

/**
 * Relevance Scorer
 * Scores evidence items for relevance to a specific contract
 */
export class RelevanceScorer {
  private config: RelevanceScorerConfig;

  constructor(config: Partial<RelevanceScorerConfig> = {}) {
    this.config = {
      ...DEFAULT_CONFIG,
      ...config,
      weights: { ...DEFAULT_RELEVANCE_WEIGHTS, ...config.weights }
    };
  }

  /**
   * Score a single evidence item against a contract
   */
  scoreEvidence(
    evidence: EnrichedEvidenceItem,
    contract: ContractForMatching
  ): ScoredEvidenceItem {
    const breakdown = this.calculateBreakdown(evidence, contract);
    const overallScore = this.calculateOverallScore(breakdown);
    const tier = this.determineTier(overallScore);
    const rationale = this.generateRationale(breakdown, tier);

    const relevance: RelevanceScore = {
      evidenceId: evidence.evidenceId,
      overallScore,
      tier,
      breakdown,
      scoringRationale: rationale
    };

    return {
      ...evidence,
      relevance
    };
  }

  /**
   * Score multiple evidence items
   */
  scoreEvidenceArray(
    evidenceItems: EnrichedEvidenceItem[],
    contract: ContractForMatching
  ): ScoredEvidenceItem[] {
    return evidenceItems.map(item => this.scoreEvidence(item, contract));
  }

  /**
   * Calculate the breakdown of relevance factors
   */
  private calculateBreakdown(
    evidence: EnrichedEvidenceItem,
    contract: ContractForMatching
  ): RelevanceBreakdown {
    return {
      naicsScore: this.calculateNaicsScore(evidence, contract),
      agencyScore: this.calculateAgencyScore(evidence, contract),
      scopeScore: this.calculateScopeScore(evidence, contract),
      recencyScore: this.calculateRecencyScore(evidence),
      valueScore: this.calculateValueScore(evidence, contract),
      setAsideScore: this.calculateSetAsideScore(evidence, contract)
    };
  }

  /**
   * Calculate weighted overall score from breakdown
   */
  private calculateOverallScore(breakdown: RelevanceBreakdown): number {
    const { weights } = this.config;

    const weighted =
      breakdown.naicsScore * weights.naics +
      breakdown.agencyScore * weights.agency +
      breakdown.scopeScore * weights.scope +
      breakdown.recencyScore * weights.recency +
      breakdown.valueScore * weights.value +
      breakdown.setAsideScore * weights.setAside;

    // Normalize to 0-1
    return Math.min(1, Math.max(0, weighted));
  }

  /**
   * Determine relevance tier based on overall score
   */
  private determineTier(score: number): RelevanceTier {
    if (score >= RELEVANCE_TIER_THRESHOLDS.HIGH) return 'HIGH';
    if (score >= RELEVANCE_TIER_THRESHOLDS.MEDIUM) return 'MEDIUM';
    if (score >= RELEVANCE_TIER_THRESHOLDS.LOW) return 'LOW';
    return 'MINIMAL';
  }

  /**
   * Calculate NAICS similarity score
   */
  private calculateNaicsScore(
    evidence: EnrichedEvidenceItem,
    contract: ContractForMatching
  ): number {
    if (!contract.naicsCode) return 0.5; // Neutral if contract has no NAICS

    const evidenceNaics = evidence.metadata.naicsCode;
    if (!evidenceNaics) return 0.3; // Low score if evidence has no NAICS

    return calculateNaicsSimilarity(evidenceNaics, contract.naicsCode);
  }

  /**
   * Calculate agency match score
   */
  private calculateAgencyScore(
    evidence: EnrichedEvidenceItem,
    contract: ContractForMatching
  ): number {
    if (!contract.agency) return 0.5; // Neutral if no agency specified

    const evidenceAgency = evidence.metadata.agencyName;
    if (!evidenceAgency) return 0.3; // Low if no agency in evidence

    // Normalize agency names for comparison
    const normalizedContract = contract.agency.toLowerCase();
    const normalizedEvidence = evidenceAgency.toLowerCase();

    // Exact match
    if (normalizedContract === normalizedEvidence) return 1.0;

    // Check if both map to same agency in taxonomy
    const contractAgency = findAgency(contract.agency);
    const evidenceAgencyNode = findAgency(evidenceAgency);

    if (contractAgency && evidenceAgencyNode) {
      // Same agency
      if (contractAgency.id === evidenceAgencyNode.id) return 1.0;

      // Same department
      if (contractAgency.department && contractAgency.department === evidenceAgencyNode.department) {
        return 0.8;
      }

      // Same domain
      if (contractAgency.domain === evidenceAgencyNode.domain) {
        return 0.6;
      }
    }

    // Check if evidence agency is a sub-agency of contract agency or vice versa
    if (normalizedEvidence.includes(normalizedContract) ||
        normalizedContract.includes(normalizedEvidence)) {
      return 0.7;
    }

    // Partial match (some words in common)
    const contractWords = new Set(normalizedContract.split(/\s+/));
    const evidenceWords = new Set(normalizedEvidence.split(/\s+/));
    const commonWords = [...contractWords].filter(w => evidenceWords.has(w) && w.length > 2);

    if (commonWords.length > 0) {
      return 0.4 + (commonWords.length * 0.1);
    }

    // Both federal but different agencies
    if (contractAgency && evidenceAgencyNode) {
      return 0.3;
    }

    return 0.2;
  }

  /**
   * Calculate scope/keyword similarity score
   */
  private calculateScopeScore(
    evidence: EnrichedEvidenceItem,
    contract: ContractForMatching
  ): number {
    if (!contract.description && contract.requirements.length === 0) {
      return 0.5; // Neutral if no scope info
    }

    // Build contract keywords from description and requirements
    const contractKeywords = this.extractKeywords(
      `${contract.title} ${contract.description || ''} ${(contract.requirements || []).map(r => r.text).join(' ')}`
    );

    if (contractKeywords.length === 0) return 0.5;

    // Evidence keywords (direct + inferred)
    const evidenceKeywords = new Set([
      ...(evidence.keywords || []),
      ...(evidence.allCapabilities || [])
    ]);

    // Calculate overlap
    const matches = contractKeywords.filter(k =>
      [...evidenceKeywords].some(ek =>
        ek.toLowerCase().includes(k.toLowerCase()) ||
        k.toLowerCase().includes(ek.toLowerCase())
      )
    );

    const overlapRatio = matches.length / contractKeywords.length;

    // Also check content similarity
    const contentScore = this.calculateContentSimilarity(
      evidence.fullContent,
      `${contract.title} ${contract.description || ''}`
    );

    // Weighted combination
    return Math.min(1, (overlapRatio * 0.6) + (contentScore * 0.4));
  }

  /**
   * Calculate recency score based on evidence age
   */
  private calculateRecencyScore(evidence: EnrichedEvidenceItem): number {
    const dateStr = evidence.createdAt || evidence.metadata.dateCreated;
    if (!dateStr) return 0.5; // Neutral if no date

    try {
      const evidenceDate = new Date(dateStr);
      const now = new Date();
      const yearsOld = (now.getTime() - evidenceDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000);

      if (yearsOld < 1) return 1.0;     // Less than 1 year old
      if (yearsOld < 2) return 0.9;     // 1-2 years old
      if (yearsOld < 3) return 0.7;     // 2-3 years old
      if (yearsOld < 5) return 0.5;     // 3-5 years old
      return 0.3;                        // 5+ years old
    } catch {
      return 0.5;
    }
  }

  /**
   * Calculate contract value similarity score
   */
  private calculateValueScore(
    evidence: EnrichedEvidenceItem,
    contract: ContractForMatching
  ): number {
    if (!contract.fundingAmount || contract.fundingAmount <= 0) {
      return 0.5; // Neutral if no contract value
    }

    const evidenceValue = evidence.metadata.contractValue;
    if (!evidenceValue || evidenceValue <= 0) {
      return 0.3; // Low if no evidence value
    }

    // Calculate ratio
    const ratio = Math.min(evidenceValue, contract.fundingAmount) /
                  Math.max(evidenceValue, contract.fundingAmount);

    // Score based on how close the values are
    if (ratio >= 0.5) return 1.0;     // Within 50% of each other
    if (ratio >= 0.25) return 0.8;    // Within 25% (4x difference)
    if (ratio >= 0.1) return 0.6;     // Within 10% (10x difference)
    if (ratio >= 0.05) return 0.4;    // Within 5% (20x difference)
    return 0.2;                        // Very different scales
  }

  /**
   * Calculate set-aside match score
   */
  private calculateSetAsideScore(
    evidence: EnrichedEvidenceItem,
    contract: ContractForMatching
  ): number {
    const contractSetAsides = contract.setAsides || [];
    const evidenceSetAsides = evidence.metadata.setAsides || [];

    if (contractSetAsides.length === 0) {
      return 0.5; // Neutral if no set-aside specified
    }

    if (evidenceSetAsides.length === 0) {
      return 0.3; // Low if evidence has no set-aside info
    }

    // Normalize set-asides for comparison
    const normalizeSetAside = (sa: string) => sa.toLowerCase().replace(/[^a-z0-9]/g, '');

    const contractNormalized = contractSetAsides.map(normalizeSetAside);
    const evidenceNormalized = evidenceSetAsides.map(normalizeSetAside);

    // Check for exact matches
    const matches = contractNormalized.filter(c => evidenceNormalized.includes(c));

    if (matches.length > 0) {
      return 1.0; // Has at least one matching set-aside
    }

    // Check for related set-asides (all small business categories)
    const sbCategories = ['sb', 'sdb', '8a', 'hubzone', 'sdvosb', 'vosb', 'wosb', 'edwosb'];
    const contractHasSB = contractNormalized.some(c => sbCategories.includes(c));
    const evidenceHasSB = evidenceNormalized.some(e => sbCategories.includes(e));

    if (contractHasSB && evidenceHasSB) {
      return 0.6; // Both have small business set-asides, even if different
    }

    return 0.3;
  }

  /**
   * Extract keywords from text
   */
  private extractKeywords(text: string): string[] {
    const stopWords = new Set([
      'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
      'of', 'with', 'by', 'from', 'as', 'is', 'was', 'are', 'were', 'been',
      'be', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would',
      'could', 'should', 'may', 'might', 'must', 'shall', 'this', 'that',
      'these', 'those', 'it', 'its', 'they', 'them', 'their', 'we', 'us',
      'our', 'you', 'your', 'all', 'any', 'some', 'such', 'no', 'not'
    ]);

    const words = text.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, ' ')
      .split(/\s+/)
      .filter(w => w.length > 2 && !stopWords.has(w));

    return [...new Set(words)];
  }

  /**
   * Calculate simple content similarity
   */
  private calculateContentSimilarity(text1: string, text2: string): number {
    const words1 = new Set(this.extractKeywords(text1));
    const words2 = new Set(this.extractKeywords(text2));

    if (words1.size === 0 || words2.size === 0) return 0;

    const intersection = [...words1].filter(w => words2.has(w));
    const union = new Set([...words1, ...words2]);

    // Jaccard similarity
    return intersection.length / union.size;
  }

  /**
   * Generate human-readable scoring rationale
   */
  private generateRationale(breakdown: RelevanceBreakdown, tier: RelevanceTier): string {
    const parts: string[] = [];

    // NAICS
    if (breakdown.naicsScore >= 0.8) {
      parts.push('Strong NAICS match');
    } else if (breakdown.naicsScore >= 0.5) {
      parts.push('Related NAICS code');
    } else if (breakdown.naicsScore < 0.4) {
      parts.push('Different industry code');
    }

    // Agency
    if (breakdown.agencyScore >= 0.8) {
      parts.push('same or related agency');
    } else if (breakdown.agencyScore >= 0.5) {
      parts.push('similar agency domain');
    }

    // Scope
    if (breakdown.scopeScore >= 0.7) {
      parts.push('strong scope alignment');
    } else if (breakdown.scopeScore >= 0.5) {
      parts.push('some scope overlap');
    }

    // Recency
    if (breakdown.recencyScore >= 0.8) {
      parts.push('recent experience');
    } else if (breakdown.recencyScore < 0.5) {
      parts.push('older experience');
    }

    // Value
    if (breakdown.valueScore >= 0.8) {
      parts.push('similar contract scale');
    } else if (breakdown.valueScore < 0.4) {
      parts.push('different contract scale');
    }

    // Set-aside
    if (breakdown.setAsideScore >= 0.8) {
      parts.push('matching set-aside');
    }

    const tierDescription = {
      HIGH: 'Highly relevant',
      MEDIUM: 'Moderately relevant',
      LOW: 'Somewhat relevant',
      MINIMAL: 'Limited relevance'
    };

    return `${tierDescription[tier]}: ${parts.join(', ') || 'general federal experience'}`;
  }
}

// Export singleton instance
export const relevanceScorer = new RelevanceScorer();

// Export convenience functions
export function scoreEvidenceRelevance(
  evidence: EnrichedEvidenceItem,
  contract: ContractForMatching
): ScoredEvidenceItem {
  return relevanceScorer.scoreEvidence(evidence, contract);
}

export function scoreEvidenceArrayRelevance(
  evidenceItems: EnrichedEvidenceItem[],
  contract: ContractForMatching
): ScoredEvidenceItem[] {
  return relevanceScorer.scoreEvidenceArray(evidenceItems, contract);
}

/**
 * Group scored evidence by tier
 */
export function groupByTier(scoredEvidence: ScoredEvidenceItem[]): Record<RelevanceTier, ScoredEvidenceItem[]> {
  const result: Record<RelevanceTier, ScoredEvidenceItem[]> = {
    HIGH: [],
    MEDIUM: [],
    LOW: [],
    MINIMAL: []
  };

  for (const item of scoredEvidence) {
    result[item.relevance.tier].push(item);
  }

  // Sort each tier by overall score descending
  for (const tier of Object.keys(result) as RelevanceTier[]) {
    result[tier].sort((a, b) => b.relevance.overallScore - a.relevance.overallScore);
  }

  return result;
}
